import { Container, Flex, Text, VStack } from "@chakra-ui/react";
import { useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { DashboardTable } from "../components/DashboardTable";
import { DripDashboardSection } from "../features/drip/DripDashboardSection";
import { DripDepositorsSection } from "../features/drip/DripDepositorsSection";
import { useNodeOperators } from "../hooks/useNodeOperators";
import { useDripVaultStates } from "../hooks/useDripVaultStates";
import { useDripDepositors } from "../hooks/useDripDepositors";
import { useKyrosTokens } from "../hooks/useKyrosTokens";
import { useErrorToast } from "../helpers/toast";
import { DRIP_TEXT_SECONDARY } from "../features/drip/dripStyles";

const SectionHeading = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <VStack align="start" spacing={1}>
    <Text fontWeight="semibold" fontSize="2xl" color="white" lineHeight="1.2">
      {title}
    </Text>
    <Text fontSize="sm" color={DRIP_TEXT_SECONDARY}>
      {subtitle}
    </Text>
  </VStack>
);

export const DashboardPage = () => {
  const { publicKey } = useWallet();
  const { nodeOperators, isLoading, error, fetchNodeOperators } = useNodeOperators();
  const {
    vaultStates,
    isLoading: dripLoading,
    error: dripError,
    fetchVaultStates,
  } = useDripVaultStates();
  const {
    positions,
    isLoading: depositorsLoading,
    error: depositorsError,
    fetchDepositors,
  } = useDripDepositors();
  const { tokens: kyrosTokens } = useKyrosTokens();
  const { errorToast } = useErrorToast();

  useEffect(() => {
    if (error) errorToast(error);
    if (dripError) errorToast(dripError);
    if (depositorsError) errorToast(depositorsError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, dripError, depositorsError]);

  useEffect(() => {
    fetchNodeOperators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey]);

  useEffect(() => {
    fetchVaultStates();
  }, [fetchVaultStates]);

  useEffect(() => {
    fetchDepositors();
  }, [fetchDepositors]);

  // Vault principals are in different LSTs, so only their SOL/USD values add up.
  const dripTotalUsd = vaultStates.every((v) => v.stakedUsd == null)
    ? null
    : vaultStates.reduce((sum, v) => sum + (v.stakedUsd ?? 0), 0);

  return (
    <Flex direction="column" minHeight="100vh">
      <Container maxW="900px" flex="1" py={8}>
        <VStack spacing={10} align="stretch">
          <VStack spacing={5} align="stretch">
            <SectionHeading
              title="Restaking"
              subtitle="Node operators holding TVL across the Kyros restaking vaults."
            />
            <DashboardTable nodeOperators={nodeOperators} isLoading={isLoading} />
          </VStack>

          <VStack spacing={5} align="stretch">
            <SectionHeading
              title="Drip"
              subtitle="Live vault state read on-chain from the Drip program."
            />
            {dripTotalUsd != null && !dripLoading && (
              <Text fontSize="sm" color={DRIP_TEXT_SECONDARY} mb={3}>
                Total across {vaultStates.length} vault
                {vaultStates.length === 1 ? "" : "s"}: ~$
                {dripTotalUsd.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </Text>
            )}
            <VStack spacing={4} align="stretch">
              {(dripLoading && vaultStates.length === 0
                ? [null]
                : vaultStates
              ).map((vs, index) => (
                <DripDashboardSection
                  key={vs?.vaultId ?? `loading-${index}`}
                  vaultState={vs}
                  isLoading={dripLoading}
                />
              ))}
            </VStack>
          </VStack>

          <VStack spacing={5} align="stretch">
            <SectionHeading
              title="Drip activity"
              subtitle="Who's dripping and into which reward token — from on-chain positions."
            />
            <DripDepositorsSection
              positions={positions}
              kyrosTokens={kyrosTokens}
              isLoading={depositorsLoading || dripLoading}
            />
          </VStack>
        </VStack>
      </Container>
    </Flex>
  );
};
